import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { useAppDownload } from '@/context/AppDownloadContext';
import { listenerProfiles, listenerTiers, tierColorMap } from '@/mocks/listenerTiers';
import { vipClubs, vipClubsSummary } from '@/mocks/vipClubs';
import { topFansAll, topFanRadios } from '@/mocks/topFansRanking';
import { badgeCollection, badgeStats } from '@/mocks/listenerBadges';
import { listenerLevels } from '@/mocks/listenerLevels';

type GamFilter = 'global' | 'radio' | 'locutor' | 'programa';
type GamTab = 'top-fans' | 'vip' | 'embajadores' | 'insignias' | 'ranking';

export default function GamificationCenterPage() {
  const { openModal: openDownloadModal } = useAppDownload();
  const [activeTab, setActiveTab] = useState<GamTab>('top-fans');
  const [filterMode, setFilterMode] = useState<GamFilter>('global');
  const [selectedRadioId, setSelectedRadioId] = useState<number>(topFanRadios[0].id);

  const radioOptions = useMemo(() => [
    { id: 0, name: 'Todas las Radios', frequency: '' },
    ...topFanRadios.map((r) => ({ id: r.id, name: r.name, frequency: r.frequency })),
  ], []);

  const selectedRadio = radioOptions.find((r) => r.id === selectedRadioId) || radioOptions[0];

  const filteredTopFans = useMemo(() => {
    if (filterMode === 'global' || filterMode === 'programa' || filterMode === 'locutor') {
      return [...topFansAll].sort((a, b) => b.totalScore - a.totalScore).slice(0, 15);
    }
    if (selectedRadioId === 0) {
      return [...topFansAll].sort((a, b) => b.totalScore - a.totalScore).slice(0, 15);
    }
    return topFansAll
      .filter((f) => f.radioId === selectedRadioId)
      .sort((a, b) => b.totalScore - a.totalScore);
  }, [filterMode, selectedRadioId]);

  const embajadores = useMemo(() => {
    let list = listenerProfiles.filter((p) => p.tierLevel >= 5);
    if (filterMode === 'radio' && selectedRadioId !== 0) {
      const radioName = topFanRadios.find((r) => r.id === selectedRadioId)?.name;
      if (radioName) list = list.filter((p) => p.mainRadio === radioName);
    }
    return list;
  }, [filterMode, selectedRadioId]);

  const filteredVIP = useMemo(() => {
    if (filterMode === 'radio' && selectedRadioId !== 0) {
      const radioName = topFanRadios.find((r) => r.id === selectedRadioId)?.name;
      if (radioName) return vipClubs.filter((c) => c.radioName === radioName && c.status === 'active');
    }
    return vipClubs.filter((c) => c.status === 'active');
  }, [filterMode, selectedRadioId]);

  const formatScore = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return `${n}`;
  };

  const tabs: { key: GamTab; label: string; icon: string; color: string; bg: string }[] = [
    { key: 'top-fans', label: 'Top Fans', icon: 'ri-star-line', color: 'text-amber-600', bg: 'bg-amber-500' },
    { key: 'vip', label: 'Club VIP', icon: 'ri-vip-crown-line', color: 'text-rose-600', bg: 'bg-rose-500' },
    { key: 'embajadores', label: 'Embajadores', icon: 'ri-shield-star-line', color: 'text-violet-600', bg: 'bg-violet-500' },
    { key: 'insignias', label: 'Insignias', icon: 'ri-award-line', color: 'text-emerald-600', bg: 'bg-emerald-500' },
    { key: 'ranking', label: 'Ranking', icon: 'ri-medal-line', color: 'text-orange-600', bg: 'bg-orange-500' },
  ];

  const filters: { key: GamFilter; label: string; icon: string }[] = [
    { key: 'global', label: 'Global', icon: 'ri-global-line' },
    { key: 'radio', label: 'Por Radio', icon: 'ri-radio-line' },
    { key: 'locutor', label: 'Por Locutor', icon: 'ri-mic-line' },
    { key: 'programa', label: 'Por Programa', icon: 'ri-tv-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-16 md:pt-18">
        {/* ── HERO ── */}
        <section className="relative w-full h-[340px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dramatic%20stadium%20concert%20crowd%20with%20hands%20raised%20under%20golden%20amber%20spotlights%2C%20energetic%20community%20atmosphere%2C%20warm%20celebratory%20lighting%2C%20wide%20angle%20view%20of%20massive%20engaged%20audience%2C%20festival%20celebration%20energy%2C%20inclusive%20diverse%20crowd%20united%20by%20music%20and%20passion%2C%20cinematic%20documentary%20style%20photography&width=1600&height=900&seq=gam-center-hero&orientation=landscape"
            alt="Centro de Gamificación RFW"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
            <div className="w-16 h-16 md:w-18 md:h-18 rounded-2xl bg-background-50/10 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/15">
              <i className="ri-trophy-line text-3xl text-background-50"></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 leading-tight mb-3">
              Centro de Gamificación
            </h1>
            <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed mb-5">
              Donde la comunidad compite, crece y se destaca. Top Fans, Embajadores, Club VIP,
              Insignias y Rankings en un solo lugar.
            </p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-star-line mr-1"></i>
                {topFansAll.length}+ Top Fans
              </span>
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-vip-crown-line mr-1"></i>
                {vipClubsSummary.activeClubs} Clubes VIP
              </span>
              <span className="px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                <i className="ri-shield-star-line mr-1"></i>
                {embajadores.length} Embajadores
              </span>
            </div>
          </div>
        </section>

        {/* ── TABS ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 -mt-6 relative z-10">
          <div className="bg-background-50 rounded-2xl p-1.5 shadow-sm">
            <div className="flex flex-wrap gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key
                      ? `${tab.bg} text-background-50 shadow-sm`
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ── FILTER BAR ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-4">
          <div className="bg-background-50 rounded-2xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {filters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilterMode(f.key)}
                  className={`flex items-center gap-1 px-3.5 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    filterMode === f.key
                      ? 'bg-foreground-900 text-background-50 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${f.icon} text-xs`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            {filterMode === 'radio' && (
              <select
                value={selectedRadioId}
                onChange={(e) => setSelectedRadioId(Number(e.target.value))}
                className="px-3 py-2 bg-background-100 rounded-xl text-xs text-foreground-800 font-medium border border-transparent focus:border-primary-500 focus:outline-none cursor-pointer"
              >
                {radioOptions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} {r.frequency ? `· ${r.frequency}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </section>

        {/* ── CONTENT ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mt-6 mb-12">
          {/* TOP FANS TAB */}
          {activeTab === 'top-fans' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-star-line text-amber-500"></i>
                  {filterMode === 'radio' && selectedRadioId !== 0
                    ? `Top Fans de ${selectedRadio.name}`
                    : 'Top Fans Globales'}
                </h3>
                <span className="text-xs text-foreground-500">{filteredTopFans.length} fans</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredTopFans.map((fan, idx) => {
                  const isPodium = idx < 3;
                  const podiumColors = ['border-amber-400 bg-amber-50/50', 'border-slate-300 bg-slate-50/30', 'border-orange-400 bg-orange-50/30'];
                  return (
                    <div
                      key={fan.id}
                      className={`bg-background-50 rounded-2xl p-4 flex items-center gap-3 transition-all hover:shadow-sm ${
                        isPodium ? `border ${podiumColors[idx]}` : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                        idx === 0 ? 'bg-amber-500 text-background-50' :
                        idx === 1 ? 'bg-slate-400 text-background-50' :
                        idx === 2 ? 'bg-orange-400 text-background-50' :
                        'bg-background-100 text-foreground-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <img
                        src={fan.photoUrl}
                        alt={fan.name}
                        className="w-11 h-11 rounded-xl object-cover object-top shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-foreground-950 truncate">{fan.name}</div>
                        <div className="text-[10px] text-foreground-500">{fan.username}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-foreground-400 flex items-center gap-0.5">
                            <i className="ri-time-line"></i>
                            {fan.hoursListened}h
                          </span>
                          <span className="text-[10px] text-foreground-400 flex items-center gap-0.5">
                            <i className="ri-chat-1-line"></i>
                            {fan.interactions}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-extrabold text-foreground-950">{fan.totalScore}</div>
                        <div className="text-[10px] text-foreground-400">pts</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredTopFans.length === 0 && (
                <div className="text-center py-10 bg-background-50 rounded-2xl">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-user-search-line text-foreground-400 text-xl"></i>
                  </div>
                  <p className="text-sm text-foreground-500">No hay Top Fans para este filtro</p>
                </div>
              )}
            </div>
          )}

          {/* VIP TAB */}
          {activeTab === 'vip' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-vip-crown-line text-rose-500"></i>
                  {filterMode === 'radio' && selectedRadioId !== 0
                    ? `Club VIP de ${selectedRadio.name}`
                    : 'Clubes VIP Activos'}
                </h3>
                <Link to="/club-vip" className="text-xs text-rose-600 font-medium hover:text-rose-700 transition-colors cursor-pointer flex items-center gap-1">
                  Ver todos <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredVIP.map((club) => (
                  <div key={club.id} className="bg-background-50 rounded-2xl overflow-hidden group hover:shadow-sm transition-all">
                    <div className="relative h-28 overflow-hidden">
                      <img src={club.thumbnailUrl} alt={club.clubName} className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-transparent"></div>
                      <div className="absolute bottom-3 left-4">
                        <span className="text-[10px] text-foreground-500">{club.radioName} · {club.radioFrequency}</span>
                      </div>
                    </div>
                    <div className="px-4 pb-4">
                      <h4 className="text-sm font-extrabold text-foreground-950 mb-1">{club.clubName}</h4>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{club.description}</p>
                      <div className="flex items-center justify-between mb-3">
                        {club.monthlyPrice === 0 ? (
                          <span className="text-sm font-extrabold text-emerald-600">Gratis</span>
                        ) : (
                          <span className="text-sm font-extrabold text-foreground-950">${club.monthlyPrice}<span className="text-[10px] font-medium text-foreground-400">/mes</span></span>
                        )}
                        <span className="text-xs text-foreground-500">{club.membersCount.toLocaleString()} miembros</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {club.benefits.exclusiveContent && (
                          <span className="px-2 py-0.5 bg-rose-50 text-rose-600 rounded-md text-[10px] font-semibold">{club.benefits.exclusiveContentCount} contenidos</span>
                        )}
                        {club.benefits.exclusiveEvents && (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded-md text-[10px] font-semibold">{club.benefits.exclusiveEventsCount} eventos</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {filteredVIP.length === 0 && (
                <div className="text-center py-10 bg-background-50 rounded-2xl">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                    <i className="ri-vip-crown-line text-foreground-400 text-xl"></i>
                  </div>
                  <p className="text-sm text-foreground-500">No hay clubes VIP activos para este filtro</p>
                </div>
              )}
            </div>
          )}

          {/* EMBAJADORES TAB */}
          {activeTab === 'embajadores' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-shield-star-line text-violet-500"></i>
                  Embajadores RFW
                </h3>
                <span className="text-xs text-foreground-500">{embajadores.length} embajadores</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {embajadores.map((profile) => {
                  const tier = listenerTiers.find((t) => t.id === profile.tierId);
                  const tierColors = tierColorMap[profile.tierName] || { bg: 'bg-violet-50', text: 'text-violet-600' };
                  return (
                    <div key={profile.id} className="bg-background-50 rounded-2xl p-5 flex items-start gap-4">
                      <div className="relative shrink-0">
                        <img
                          src={profile.photoUrl}
                          alt={profile.name}
                          className="w-14 h-14 rounded-2xl object-cover object-top"
                        />
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center border-2 border-background-50">
                          <i className="ri-vip-crown-line text-background-50 text-[10px]"></i>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="text-sm font-bold text-foreground-950 truncate">{profile.name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tierColors.bg} ${tierColors.text}`}>
                            {profile.tierName}
                          </span>
                        </div>
                        <div className="text-[10px] text-foreground-500 mb-2">{profile.username}</div>
                        <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line text-foreground-400"></i>
                            {formatScore(profile.hoursListened)}h
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-1-line text-foreground-400"></i>
                            {formatScore(profile.comments)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-trophy-line text-foreground-400"></i>
                            {profile.participations}
                          </span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-background-100 flex items-center gap-1.5 text-[10px]">
                          <i className="ri-radio-line text-foreground-400"></i>
                          <span className="text-foreground-500">{profile.mainRadio}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {embajadores.length === 0 && (
                <div className="text-center py-10 bg-background-50 rounded-2xl">
                  <p className="text-sm text-foreground-500">No hay Embajadores para este filtro</p>
                </div>
              )}
            </div>
          )}

          {/* INSIGNIAS TAB */}
          {activeTab === 'insignias' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-award-line text-emerald-500"></i>
                  Insignias de la Comunidad
                </h3>
                <Link to="/insignias" className="text-xs text-emerald-600 font-medium hover:text-emerald-700 transition-colors cursor-pointer flex items-center gap-1">
                  Ver colección completa <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {badgeCollection.map((badge) => (
                  <div
                    key={badge.id}
                    className={`bg-background-50 rounded-2xl p-5 text-center transition-all ${
                      badge.earned ? 'hover:shadow-sm' : 'opacity-60'
                    }`}
                  >
                    <div className={`w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center ${
                      badge.earned ? badge.color : 'bg-foreground-300'
                    }`}>
                      <i className={`${badge.icon} ${badge.earned ? 'text-background-50' : 'text-background-100'} text-xl`}></i>
                    </div>
                    <h4 className={`text-sm font-bold mb-1 ${badge.earned ? 'text-foreground-950' : 'text-foreground-500'}`}>
                      {badge.name}
                    </h4>
                    <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{badge.description}</p>
                    {badge.earned ? (
                      <span className="inline-block mt-2 px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full text-[10px] font-bold">
                        Desbloqueada
                      </span>
                    ) : (
                      <div className="mt-2">
                        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${badge.color}`} style={{ width: `${badge.progress}%` }}></div>
                        </div>
                        <span className="text-[10px] text-foreground-400 mt-0.5">{badge.progressLabel}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* RANKING TAB */}
          {activeTab === 'ranking' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-extrabold text-foreground-950 flex items-center gap-2">
                  <i className="ri-medal-line text-orange-500"></i>
                  Ranking de Oyentes
                </h3>
                <Link to="/niveles" className="text-xs text-orange-600 font-medium hover:text-orange-700 transition-colors cursor-pointer flex items-center gap-1">
                  Ver niveles <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="bg-background-50 rounded-2xl overflow-hidden">
                {listenerProfiles
                  .sort((a, b) => b.hoursListened - a.hoursListened)
                  .slice(0, 20)
                  .map((profile, idx) => {
                    const tier = listenerTiers.find((t) => t.id === profile.tierId);
                    const isPodium = idx < 3;
                    return (
                      <div
                        key={profile.id}
                        className={`flex items-center gap-3 px-5 py-3.5 ${
                          idx % 2 === 0 ? 'bg-background-50' : 'bg-background-100/30'
                        } ${isPodium ? 'border-l-2 border-amber-400' : ''}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center shrink-0">
                          <span className={`text-xs font-bold ${isPodium ? 'text-amber-600' : 'text-foreground-500'}`}>
                            #{idx + 1}
                          </span>
                        </div>
                        <img
                          src={profile.photoUrl}
                          alt={profile.name}
                          className="w-9 h-9 rounded-lg object-cover object-top shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold text-foreground-950 truncate">{profile.name}</span>
                            {tier && (
                              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${tier.bgColor} ${tier.textColor}`}>
                                {profile.tierName}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-foreground-500">
                            <span>{formatScore(profile.hoursListened)}h</span>
                            <span>{formatScore(profile.comments)} msjs</span>
                            <span>{profile.participations} part.</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-sm font-extrabold text-foreground-950">
                            {profile.hoursListened}
                          </div>
                          <div className="text-[10px] text-foreground-400">horas</div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </section>

        {/* ── CROSS-LINKS ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link
              to="/gamificacion"
              className="bg-background-50 rounded-2xl p-5 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
                  <i className="ri-user-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Tu Gamificación</h4>
                  <p className="text-xs text-foreground-500">Tu perfil y puntos personales</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/niveles"
              className="bg-background-50 rounded-2xl p-5 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-500 flex items-center justify-center">
                  <i className="ri-stack-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Niveles</h4>
                  <p className="text-xs text-foreground-500">Sistema de progresión completo</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link
              to="/rankings"
              className="bg-background-50 rounded-2xl p-5 hover:shadow-sm transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                  <i className="ri-trophy-line text-background-50"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Rankings de Radios</h4>
                  <p className="text-xs text-foreground-500">Competencia entre emisoras</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 mb-12">
          <div className="bg-background-50 rounded-2xl p-8 md:p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <i className="ri-rocket-line text-background-50 text-3xl"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950 mb-2">
              ¿Listo para competir?
            </h2>
            <p className="text-sm text-foreground-500 mb-5 max-w-lg mx-auto">
              Cada interacción suma. Escuchá, participá, compartí y subí en el ranking.
              La comunidad RFW te reconoce por tu dedicación.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={openDownloadModal}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line mr-1.5"></i>
                Descargar App RFW
              </button>
              <Link
                to="/insignias"
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-award-line mr-1.5"></i>
                Ver Insignias
              </Link>
              <Link
                to="/club-vip"
                className="px-5 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-vip-crown-line mr-1.5"></i>
                Club VIP
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <div className="md:hidden h-20"></div>
    </div>
  );
}